import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../context/AuthContext";
import Countdown from "../components/Countdown";
import AuctionCard from "../components/AuctionCard";
import { useLanguage } from "../context/LanguageContext";

const fmt = (n, language) => new Intl.NumberFormat(language === "en" ? "en-IE" : "it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

export default function Dashboard() {
  const { language, t } = useLanguage();
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("bids");
  const [bids, setBids] = useState([]);
  const [listings, setListings] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [resending, setResending] = useState(false);

  const resendVerification = async () => {
    setResending(true);
    try {
      const { data } = await axios.post(`${API}/auth/resend-verification`);
      if (data.email_sent) toast.success(t("Email di verifica inviata. Controlla la tua casella."));
      else toast.info(t("Sistema email in configurazione. Riprova più tardi."));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Errore invio email"));
    } finally { setResending(false); }
  };

  useEffect(() => {
    if (ready && (!user || user === false)) nav("/login");
  }, [ready, user, nav]);

  useEffect(() => {
    if (!user || user === false) return;
    axios.get(`${API}/me/bids`).then(r => setBids(r.data));
    axios.get(`${API}/me/listings`).then(r => setListings(r.data));
    axios.get(`${API}/me/watchlist`).then(r => setWatchlist(r.data));
  }, [user]);

  if (!user || user === false) return null;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
      {user.email_verified === false && (
        <div data-testid="verify-banner" className="mb-8 border-l-4 border-[#8C3A3A] bg-[#8C3A3A]/5 p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="overline text-[#8C3A3A]">{t("Email non verificata")}</div>
            <div className="mt-1 text-sm text-stone-700">{t("Verifica il tuo indirizzo ")}<strong>{user.email}</strong>{t(" per pubblicare lotti in asta.")}</div>
          </div>
          <button
            data-testid="resend-verification-btn"
            onClick={resendVerification}
            disabled={resending}
            className="text-xs uppercase tracking-widest font-semibold bg-stone-900 hover:bg-[#8C3A3A] text-stone-50 px-6 py-3 transition-colors disabled:opacity-50 whitespace-nowrap"
          >{resending ? t("Invio…") : t("Reinvia email")}</button>
        </div>
      )}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="overline text-stone-500">{t("Ciao, ")}{user.name}</div>
          <h1 className="font-serif text-5xl tracking-tighter mt-2">{t("Il tuo studio.")}</h1>
        </div>
        <Link to="/sell" data-testid="dashboard-sell-btn" className="text-xs uppercase tracking-widest font-semibold bg-[#8C3A3A] hover:bg-[#7F3434] text-stone-50 px-8 py-4 transition-colors">{t("Metti in asta")}</Link>
      </div>

      <div className="mt-10 border-b border-stone-200 flex gap-8">
        <TabBtn active={tab==="bids"} onClick={()=>setTab("bids")} testId="tab-bids">{t("Le mie offerte (")}{bids.length})</TabBtn>
        <TabBtn active={tab==="listings"} onClick={()=>setTab("listings")} testId="tab-listings">{t("I miei lotti (")}{listings.length})</TabBtn>
        <TabBtn active={tab==="watchlist"} onClick={()=>setTab("watchlist")} testId="tab-watchlist">{t("Preferiti (")}{watchlist.length})</TabBtn>
      </div>

      <div className="mt-10">
        {tab === "watchlist" ? (
          watchlist.length === 0 ? (
            <Empty msg={t("Non hai ancora salvato lotti.")} cta={<Link to="/browse" className="underline-hover text-[#8C3A3A]">{t("Esplora aste")}</Link>} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
              {watchlist.map((a, i) => <AuctionCard key={a.id} auction={a} index={i} />)}
            </div>
          )
        ) : tab === "bids" ? (
          bids.length === 0 ? (
            <Empty msg={t("Nessuna offerta ancora.")} cta={<Link to="/browse" className="underline-hover text-[#8C3A3A]">{t("Esplora aste")}</Link>} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {bids.map((b, i) => (
                <div key={i} className="border border-stone-200 p-6 flex gap-4 bg-white">
                  <img src={b.auction.image_url} className="w-24 h-32 object-cover" alt="" />
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <Link to={`/auction/${b.auction.id}`} className="font-serif text-xl hover:text-[#8C3A3A] transition-colors leading-tight">{b.auction.title}</Link>
                      <div className="text-xs text-stone-500 mt-1 capitalize">{b.auction.category.replace(/-/g," ")}</div>
                    </div>
                    <div className="flex items-end justify-between mt-4">
                      <div>
                        <div className="overline text-stone-500">{t("La tua offerta")}</div>
                        <div className="font-mono">{fmt(b.my_bid, language)}</div>
                      </div>
                      <div className="text-right">
                        <div className="overline text-stone-500">{t("Stato")}</div>
                        {b.is_top ? (
                          <div className="text-sm text-emerald-700 font-semibold">{t("Sei in testa")}</div>
                        ) : (
                          <div className="text-sm text-stone-600">{t("Superato")}</div>
                        )}
                      </div>
                      <Countdown endsAt={b.auction.ends_at} short className="text-xs" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          listings.length === 0 ? (
            <Empty msg={t("Non hai ancora messo in asta nulla.")} cta={<Link to="/sell" className="underline-hover text-[#8C3A3A]">{t("Metti in asta ora")}</Link>} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map(l => (
                <Link to={`/auction/${l.id}`} key={l.id} className="border border-stone-200 p-6 group bg-white">
                  <img src={l.image_url} className="w-full aspect-[4/5] object-cover" alt="" />
                  <div className="mt-4">
                    <h3 className="font-serif text-xl leading-tight group-hover:text-[#8C3A3A] transition-colors">{l.title}</h3>
                    <div className="mt-2 flex justify-between text-sm">
                      <span className="font-mono">{fmt(l.current_bid, language)}</span>
                      <span className="text-stone-500">{l.bid_count}{t(" offerte")}</span>
                    </div>
                    <div className="mt-2 text-xs text-stone-500 border-t border-stone-100 pt-2">
                      {t("Se aggiudicato: ")}<span className="text-stone-900 font-mono">{fmt(l.current_bid * 0.9, language)}</span>{t(" a te · ")}<span className="text-[#8C3A3A] font-mono">{fmt(l.current_bid * 0.1, language)}</span>{t(" commissione Rarium")}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}

const TabBtn = ({ active, onClick, children, testId }) => (
  <button data-testid={testId} onClick={onClick} className={`pb-3 text-sm border-b-2 -mb-px transition-colors ${active ? "border-[#8C3A3A] text-stone-900" : "border-transparent text-stone-500 hover:text-stone-900"}`}>
    {children}
  </button>
);
const Empty = ({ msg, cta }) => (
  <div className="border border-stone-200 p-16 text-center bg-white">
    <div className="font-serif text-3xl">{msg}</div>
    <div className="mt-4">{cta}</div>
  </div>
);
